import React, { useEffect, useRef } from 'react';
import Wahanda from 'common/wahanda';
import { openChat } from 'utilities/support-chat';
import style from './AppointmentHelpLink.scss';

export const APPOINTMENT_HELP_LINK_FEATURE = 'THY-2483-appointment-help-link';

interface AppointmentViewData {
  model?: { id?: number };
  orderSetupData?: unknown;
  orderSetupOrderId?: number;
}

// Opened by appointment id or from an order. The create flows pass a model without an id.
export const isExistingAppointment = (viewData?: AppointmentViewData): boolean =>
  Boolean(viewData?.model?.id || viewData?.orderSetupData || viewData?.orderSetupOrderId);

interface ShouldShowParams {
  isExistingAppointment: boolean;
  isAppointmentTab: boolean;
}

// Only an existing appointment: the create flow and block time have no booking to ask about.
export const shouldShowAppointmentHelpLink = ({
  isExistingAppointment,
  isAppointmentTab,
}: ShouldShowParams): boolean =>
  isExistingAppointment &&
  isAppointmentTab &&
  Wahanda.Features.isEnabled(APPOINTMENT_HELP_LINK_FEATURE);

// CalendarEventEditor saves on any Enter keyup that reaches document.body, and React 16 handles
// events on document, after that listener. Mark the Enter natively so the editor skips it.
const useSkipEditorEnterSave = () => {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const node = ref.current;
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    };
    node?.addEventListener('keyup', onKeyUp);
    return () => node?.removeEventListener('keyup', onKeyUp);
  }, []);

  return ref;
};

export const AppointmentHelpLink = () => {
  const ref = useSkipEditorEnterSave();

  return (
    <span className={style.container}>
      <span className={style.separator} aria-hidden="true">
        ·
      </span>
      <button
        ref={ref}
        type="button"
        className={style.link}
        data-test="appointment-help-link"
        onClick={() => openChat('edit appointment modal')}
      >
        <span className={style.avatar} aria-hidden="true" />
        {Wahanda.lang.calendar.appointmentForm.needHelp}
      </button>
    </span>
  );
};
